import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { trpc } from '@/trpc/client';
import { useLocationContext } from '@/contexts/LocationContext';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Syncs the URL :locationSlug param to LocationContext.
 * Placed inside the /location/:locationSlug route tree.
 *
 * - Resolves slug → locationId via tRPC query
 * - Updates selectedLocationId in context when URL slug changes
 * - Redirects to user's default location if slug is invalid
 */
export function LocationSync() {
  const { locationSlug } = useParams<{ locationSlug: string }>();
  const { setSelectedLocationId, selectedLocationId } = useLocationContext();
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: location, error } = trpc.location.getBySlug.useQuery(
    { slug: locationSlug! },
    { enabled: !!locationSlug, retry: false }
  );

  // Sync resolved locationId to context
  useEffect(() => {
    if (location && location.id !== selectedLocationId) {
      setSelectedLocationId(location.id);
    }
  }, [location?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Redirect to default location if slug is invalid
  useEffect(() => {
    if (error && user?.primaryLocationId) {
      // Slug not found — redirect to user's primary location
      navigate('/', { replace: true });
    }
  }, [error, user?.primaryLocationId, navigate]);

  return null;
}
